import { useMemo, useState } from 'react';
import { Tabs, Tab } from '@ds';
import { formatSuggestionDate } from '@lp/features/MultiSuggest/utils';
import { Case } from '@/api/apis/case';
import { StatisticsList } from './StatisticsList';
import { CoverageDetails } from './CoverageDetails';
import { RequestDetails } from './RequestDetails';

type MultiSuggestStatisticsProps = {
  taskId: string;
  caseList: Case[];
  chainNumber: number;
  isInProgress: boolean;
  suggestedDate: string | null;
};

export const MultiSuggestStatistics = ({
  taskId,
  caseList,
  chainNumber,
  isInProgress,
  suggestedDate,
}: MultiSuggestStatisticsProps) => {
  const [selectedTab, setSelectedTab] = useState('overall');

  const logisticsIds = useMemo(
    () => caseList.map((i) => i.logisticsReference?.id).filter((i): i is string => Boolean(i)),
    [caseList],
  );
  const caseIds = useMemo(() => caseList.map((i) => i.caseId), [caseList]);

  const headerTitle = isInProgress ? 'Calculating...' : 'Summary';

  return (
    <div className="shrink-0 w-130 p-4 rounded-l-3xl shadow-xl h-full bg-base-80 flex flex-col gap-4">
      <div className="flex justify-between items-center w-full p-2 border-b-1 border-base-50">
        <span className="text-hm text-base-20 font-secondary">{headerTitle}</span>
        {!!suggestedDate && (
          <div className="flex gap-1">
            <span className="text-s-r text-base-20">Valid for:</span>
            <span className="text-s-sb text-base-5">{formatSuggestionDate(suggestedDate)}</span>
          </div>
        )}
      </div>
      <Tabs
        onSelectionChange={(key) => setSelectedTab(key as string)}
        aria-label="Multi-Case Suggest Tabs"
        variant="underlined"
        selectedKey={selectedTab}
        classNames={{
          tabContent: 'text-s-cps group-data-[selected=true]:text-black text-base-20',
          base: 'grid w-fit',
          tabList: 'p-0 gap-0',
          tab: 'w-24 text-center py-2.5 px-0',
          panel: 'flex flex-col overflow-auto p-2 gap-10',
        }}
      >
        <Tab key="overall" title="OVERALL">
          <StatisticsList taskId={taskId} chainNumber={chainNumber} caseIds={caseIds} logisticsIds={logisticsIds} />
          <CoverageDetails
            taskId={taskId}
            chainNumber={chainNumber}
            caseIds={caseIds}
            logisticsIds={logisticsIds}
            isOverallStatistics
          />
        </Tab>
        {caseList.map((i) => (
          <Tab key={i.caseId} title={i.caseName}>
            <StatisticsList
              taskId={taskId}
              chainNumber={chainNumber}
              caseIds={[i.caseId]}
              logisticsIds={[i.logisticsReference!.id]}
            />
            <CoverageDetails
              taskId={taskId}
              chainNumber={chainNumber}
              caseIds={[i.caseId]}
              logisticsIds={[i.logisticsReference!.id]}
            />
          </Tab>
        ))}
      </Tabs>
      <RequestDetails taskId={taskId} />
    </div>
  );
};
